import html2canvas from "html2canvas";
import { jsPDF as JsPDF } from "jspdf";

import { extractEditorContent } from "./extractEditorContent";
import { getHeaderTemplate } from "./headerTemplate";
import { getDocumentStyles } from "./documentStyles";
import { buildDocumentHTML } from "./buildDocumentHTML";

const DEFAULT_LAYOUT = {
	pageFormat: "a4",
	orientation: "portrait",
	unit: "mm",
	marginTop: 12,
	marginRight: 12,
	marginBottom: 12,
	marginLeft: 12,
	headerGap: 5,
	footerGap: 5,
	contentScale: 2,
};

const PDF_DYNAMIC_AUTOFILL_SELECTOR = [
	'[data-dynamic-autofill="true"]',
	".dynamic-autofill",
	".readonly-text",
	'[data-chip="true"]',
].join(", ");

const createWorkContainer = (html) => {
	const wrapper = document.createElement("div");
	wrapper.style.position = "fixed";
	wrapper.style.left = "-10000px";
	wrapper.style.top = "0";
	wrapper.style.width = "1200px";
	wrapper.style.zIndex = "-1";
	wrapper.style.pointerEvents = "none";
	wrapper.innerHTML = html;
	document.body.appendChild(wrapper);
	return wrapper;
};

const destroyWorkContainer = (container) => {
	if (container?.parentNode) {
		container.parentNode.removeChild(container);
	}
};

const waitForAnimationFrame = () =>
	new Promise((resolve) => {
		requestAnimationFrame(() => resolve());
	});

const waitForFonts = async () => {
	if (!document.fonts?.ready) return;

	try {
		await document.fonts.ready;
	} catch (error) {
		// Continue with fallback metrics if the font promise fails.
	}
};

const waitForImage = (image) =>
	new Promise((resolve) => {
		if (image.complete) {
			resolve();
			return;
		}

		const done = () => {
			image.removeEventListener("load", done);
			image.removeEventListener("error", done);
			resolve();
		};

		image.addEventListener("load", done, { once: true });
		image.addEventListener("error", done, { once: true });
	});

const waitForResources = async (container) => {
	if (!container) return;

	await waitForFonts();
	const images = Array.from(container.querySelectorAll("img"));
	await Promise.all(images.map((image) => waitForImage(image)));
	await waitForAnimationFrame();
	await waitForAnimationFrame();
};

const cloneChildNodes = (source, target) => {
	Array.from(source.childNodes).forEach((child) => {
		target.appendChild(child.cloneNode(true));
	});
};

const paintNodeBlack = (node) => {
	node.style.removeProperty("color");
	node.style.setProperty("color", "#1f2937", "important");
};

const getMeaningfulChildren = (element) =>
	Array.from(element.childNodes).filter((child) => {
		if (child.nodeType === Node.ELEMENT_NODE) return true;
		return child.nodeType === Node.TEXT_NODE && child.textContent.trim().length > 0;
	});

const normalizeLegacyAutofillWrappers = (node, rootNode) => {
	let current = node.parentElement;

	while (
		current &&
		rootNode.contains(current) &&
		current.tagName === "SPAN" &&
		getMeaningfulChildren(current).length === 1
	) {
		paintNodeBlack(current);
		current = current.parentElement;
	}
};

const normalizeDynamicAutofillForPDF = (rootNode) => {
	if (!rootNode) return;

	rootNode.querySelectorAll(PDF_DYNAMIC_AUTOFILL_SELECTOR).forEach((node) => {
		paintNodeBlack(node);
		node.querySelectorAll("*").forEach((child) => {
			paintNodeBlack(child);
		});
		normalizeLegacyAutofillWrappers(node, rootNode);
	});
};

const createPage = ({ pagesNode, headerNode, includeHeader }) => {
	const page = document.createElement("section");
	page.className = "siis-pdf-page";

	const inner = document.createElement("div");
	inner.className = "siis-pdf-page__inner";

	if (includeHeader && headerNode?.innerHTML.trim()) {
		const header = document.createElement("div");
		header.className = "siis-pdf-page__header";
		cloneChildNodes(headerNode, header);
		inner.appendChild(header);
	}

	const content = document.createElement("div");
	content.className = "siis-pdf-page__content";

	const footerSpacer = document.createElement("div");
	footerSpacer.className = "siis-pdf-page__footer-spacer";

	inner.appendChild(content);
	inner.appendChild(footerSpacer);
	page.appendChild(inner);
	pagesNode.appendChild(page);

	return {
		page,
		content,
	};
};

const contentFitsInPage = (pageState) =>
	pageState.content.scrollHeight <= pageState.content.clientHeight + 1;

const pageHasContent = (pageState) => pageState.content.childNodes.length > 0;

const prependToQueue = (queue, items) => {
	for (let index = items.length - 1; index >= 0; index -= 1) {
		queue.unshift(items[index]);
	}
};

const createParagraphFromText = (textNode) => {
	const paragraph = document.createElement("p");
	paragraph.textContent = textNode.textContent.trim();
	return paragraph;
};

const getContentBlocks = (contentNode) => {
	const blocks = [];

	Array.from(contentNode.childNodes).forEach((node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			if (node.textContent.trim()) {
				blocks.push(createParagraphFromText(node));
			}
			return;
		}

		if (node.nodeType === Node.ELEMENT_NODE) {
			blocks.push(node);
		}
	});

	return blocks;
};

const getMeaningfulChildNodes = (element) =>
	Array.from(element.childNodes).filter((node) => {
		if (node.nodeType === Node.ELEMENT_NODE) return true;
		return node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0;
	});

const trimEdgeWhitespace = (element, edge) => {
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
	const textNodes = [];

	while (walker.nextNode()) {
		textNodes.push(walker.currentNode);
	}

	if (!textNodes.length) return;

	const targetNode = edge === "start" ? textNodes[0] : textNodes[textNodes.length - 1];
	targetNode.textContent =
		edge === "start"
			? targetNode.textContent.replace(/^\s+/, "")
			: targetNode.textContent.replace(/\s+$/, "");
};

const getTextNodes = (element) => {
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
	const textNodes = [];

	while (walker.nextNode()) {
		textNodes.push(walker.currentNode);
	}

	return textNodes;
};

const resolveTextOffset = (textNodes, absoluteOffset) => {
	let remainingOffset = absoluteOffset;

	for (let index = 0; index < textNodes.length; index += 1) {
		const textNode = textNodes[index];
		const textLength = textNode.textContent.length;

		if (remainingOffset <= textLength) {
			return {
				node: textNode,
				offset: remainingOffset,
			};
		}

		remainingOffset -= textLength;
	}

	const lastNode = textNodes[textNodes.length - 1];
	return {
		node: lastNode,
		offset: lastNode?.textContent.length ?? 0,
	};
};

const buildTextSlice = (sourceBlock, startOffset, endOffset, textNodes) => {
	if (startOffset >= endOffset) return null;

	const start = resolveTextOffset(textNodes, startOffset);
	const end = resolveTextOffset(textNodes, endOffset);
	const range = document.createRange();

	range.setStart(start.node, start.offset);
	range.setEnd(end.node, end.offset);

	const fragment = range.cloneContents();
	const slice = sourceBlock.cloneNode(false);
	slice.appendChild(fragment);
	trimEdgeWhitespace(slice, "start");
	trimEdgeWhitespace(slice, "end");

	return slice.textContent.trim() ? slice : null;
};

const getWordBreakpoints = (text) => {
	const breakpoints = [];
	const matcher = /\S+\s*/g;
	let match = matcher.exec(text);

	while (match !== null) {
		breakpoints.push(match.index + match[0].length);
		match = matcher.exec(text);
	}

	if (breakpoints.length > 0) {
		return breakpoints.filter((point) => point > 0 && point < text.length);
	}

	return Array.from({ length: Math.max(0, text.length - 1) }, (_, index) => index + 1);
};

const splitTextElementToFit = (sourceBlock, pageState, mountNode = pageState.content) => {
	const textContent = sourceBlock.textContent || "";
	const breakpoints = getWordBreakpoints(textContent);

	if (!breakpoints.length) return null;

	const textNodes = getTextNodes(sourceBlock);
	if (!textNodes.length) return null;

	let low = 0;
	let high = breakpoints.length - 1;
	let bestIndex = -1;

	while (low <= high) {
		const middle = Math.floor((low + high) / 2);
		const candidate = buildTextSlice(sourceBlock, 0, breakpoints[middle], textNodes);

		if (!candidate) {
			high = middle - 1;
		} else {
			mountNode.appendChild(candidate);
			const fits = contentFitsInPage(pageState);
			mountNode.removeChild(candidate);

			if (fits) {
				bestIndex = middle;
				low = middle + 1;
			} else {
				high = middle - 1;
			}
		}
	}

	if (bestIndex < 0) return null;

	const splitAt = breakpoints[bestIndex];
	const head = buildTextSlice(sourceBlock, 0, splitAt, textNodes);
	const tail = buildTextSlice(sourceBlock, splitAt, textContent.length, textNodes);

	if (!head || !tail) return null;

	return {
		head,
		tail,
	};
};

const splitTextBlockToFit = (sourceBlock, pageState) =>
	splitTextElementToFit(sourceBlock, pageState, pageState.content);

const canSplitByWords = (sourceBlock) => {
	if (!sourceBlock || sourceBlock.nodeType !== Node.ELEMENT_NODE) return false;
	if (sourceBlock.tagName === "TABLE") return false;
	if (sourceBlock.tagName === "UL" || sourceBlock.tagName === "OL") return false;

	const textContent = (sourceBlock.textContent || "").trim();
	if (textContent.length < 2) return false;

	const textNodes = getTextNodes(sourceBlock);
	return textNodes.length > 0;
};

const splitByChildNodesToFit = (sourceBlock, pageState) => {
	const sourceChildren = getMeaningfulChildNodes(sourceBlock);
	if (sourceChildren.length < 2) return null;

	const head = sourceBlock.cloneNode(false);
	pageState.content.appendChild(head);

	let usedChildren = 0;

	for (let index = 0; index < sourceChildren.length; index += 1) {
		const childClone = sourceChildren[index].cloneNode(true);
		head.appendChild(childClone);

		if (!contentFitsInPage(pageState)) {
			head.removeChild(childClone);
			break;
		}

		usedChildren += 1;
	}

	pageState.content.removeChild(head);

	if (usedChildren === 0 || usedChildren === sourceChildren.length) {
		return null;
	}

	const tail = sourceBlock.cloneNode(false);
	sourceChildren.slice(usedChildren).forEach((child) => {
		tail.appendChild(child.cloneNode(true));
	});

	return {
		head,
		tail,
	};
};

const splitListToFit = (sourceBlock, pageState) => {
	const listItems = Array.from(sourceBlock.children).filter((child) => child.tagName === "LI");
	if (listItems.length < 2) return null;

	const head = sourceBlock.cloneNode(false);
	pageState.content.appendChild(head);

	let usedItems = 0;

	for (let index = 0; index < listItems.length; index += 1) {
		const itemClone = listItems[index].cloneNode(true);
		head.appendChild(itemClone);

		if (!contentFitsInPage(pageState)) {
			head.removeChild(itemClone);
			break;
		}

		usedItems += 1;
	}

	pageState.content.removeChild(head);

	if (usedItems === 0 || usedItems === listItems.length) {
		return null;
	}

	const tail = sourceBlock.cloneNode(false);
	listItems.slice(usedItems).forEach((item) => {
		tail.appendChild(item.cloneNode(true));
	});

	return {
		head,
		tail,
	};
};

const buildTableWithRows = (sourceTable, rows) => {
	if (sourceTable.tBodies.length !== 1) return null;

	const table = sourceTable.cloneNode(true);
	const targetBody = table.tBodies[0];
	targetBody.innerHTML = "";

	rows.forEach((row) => {
		targetBody.appendChild(row.cloneNode(true));
	});

	return table;
};

const splitTableToFit = (sourceBlock, pageState) => {
	if (sourceBlock.tBodies.length !== 1) return null;

	const rows = Array.from(sourceBlock.tBodies[0].rows);
	if (rows.length < 2) return null;

	let usedRows = 0;

	for (let index = 1; index <= rows.length; index += 1) {
		const candidate = buildTableWithRows(sourceBlock, rows.slice(0, index));
		if (!candidate) return null;

		pageState.content.appendChild(candidate);
		const fits = contentFitsInPage(pageState);
		pageState.content.removeChild(candidate);

		if (!fits) {
			break;
		}

		usedRows = index;
	}

	if (usedRows === 0 || usedRows === rows.length) {
		return null;
	}

	return {
		head: buildTableWithRows(sourceBlock, rows.slice(0, usedRows)),
		tail: buildTableWithRows(sourceBlock, rows.slice(usedRows)),
	};
};

const splitWizardBlockToFit = (sourceBlock, pageState) => {
	const sourceBody = sourceBlock.querySelector(".wizard-block__body");
	if (!sourceBody) return null;

	const bodyChildren = getMeaningfulChildNodes(sourceBody);

	const head = sourceBlock.cloneNode(true);
	const tail = sourceBlock.cloneNode(true);
	const headBody = head.querySelector(".wizard-block__body");
	const tailBody = tail.querySelector(".wizard-block__body");

	headBody.innerHTML = "";
	tailBody.innerHTML = "";

	if (bodyChildren.length < 2) {
		const singleChild = bodyChildren[0];
		if (!singleChild || singleChild.nodeType !== Node.ELEMENT_NODE) return null;

		pageState.content.appendChild(head);
		const splitSingleChild = splitTextElementToFit(singleChild, pageState, headBody);
		pageState.content.removeChild(head);

		if (!splitSingleChild?.head || !splitSingleChild?.tail) {
			return null;
		}

		headBody.appendChild(splitSingleChild.head);
		tailBody.appendChild(splitSingleChild.tail);

		return {
			head,
			tail,
		};
	}

	pageState.content.appendChild(head);

	let usedChildren = 0;

	for (let index = 0; index < bodyChildren.length; index += 1) {
		const childClone = bodyChildren[index].cloneNode(true);
		headBody.appendChild(childClone);

		if (!contentFitsInPage(pageState)) {
			headBody.removeChild(childClone);
			break;
		}

		usedChildren += 1;
	}

	pageState.content.removeChild(head);

	if (usedChildren === 0 || usedChildren === bodyChildren.length) {
		return null;
	}

	bodyChildren.slice(usedChildren).forEach((child) => {
		tailBody.appendChild(child.cloneNode(true));
	});

	return {
		head,
		tail,
	};
};

const isTextLikeBlock = (element) => {
	const textLikeTags = ["P", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6"];

	if (textLikeTags.includes(element.tagName)) {
		return true;
	}

	return element.tagName === "DIV" && element.children.length === 0 && element.textContent.trim().length > 0;
};

const splitOversizedBlock = (sourceBlock, pageState) => {
	if (!sourceBlock || sourceBlock.nodeType !== Node.ELEMENT_NODE) return null;

	if (sourceBlock.tagName === "TABLE") {
		return splitTableToFit(sourceBlock, pageState);
	}

	if (sourceBlock.classList.contains("wizard-block")) {
		return splitWizardBlockToFit(sourceBlock, pageState);
	}

	if (sourceBlock.tagName === "UL" || sourceBlock.tagName === "OL") {
		return splitListToFit(sourceBlock, pageState);
	}

	if (isTextLikeBlock(sourceBlock) || canSplitByWords(sourceBlock)) {
		return splitTextBlockToFit(sourceBlock, pageState);
	}

	return splitByChildNodesToFit(sourceBlock, pageState);
};

const paginateBlocks = ({ headerNode, contentNode, pagesNode }) => {
	const pendingBlocks = [...getContentBlocks(contentNode)];
	const pages = [];
	const createFollowingPage = () => {
		const nextPage = createPage({
			pagesNode,
			headerNode,
			includeHeader: false,
		});
		pages.push(nextPage.page);
		return nextPage;
	};

	let currentPage = createPage({
		pagesNode,
		headerNode,
		includeHeader: true,
	});
	pages.push(currentPage.page);

	if (!pendingBlocks.length) {
		return pages;
	}

	while (pendingBlocks.length > 0) {
		const block = pendingBlocks.shift();
		const blockClone = block.cloneNode(true);

		currentPage.content.appendChild(blockClone);

		if (!contentFitsInPage(currentPage)) {
			currentPage.content.removeChild(blockClone);
			const splitResult = splitOversizedBlock(block, currentPage);

			if (splitResult?.head) {
				currentPage.content.appendChild(splitResult.head);

				if (splitResult.tail) {
					prependToQueue(pendingBlocks, [splitResult.tail]);
					currentPage = createFollowingPage();
				}
			} else if (pageHasContent(currentPage)) {
				currentPage = createFollowingPage();
				pendingBlocks.unshift(block);
			} else {
				currentPage.content.appendChild(blockClone);

				if (pendingBlocks.length > 0) {
					currentPage = createFollowingPage();
				}
			}
		}
	}

	return pages;
};

export const contentToPDF = async ({
	html = "",
	editorPayload = null,
	editorRef = null,
	editorElement = null,
	outputFileName = "documento.pdf",
	autoSave = true,
	logos = {
		headers: [],
	},
	styleOptions = {},
	layout = {},
} = {}) => {
	const finalLayout = { ...DEFAULT_LAYOUT, ...layout };

	const editorHtml = extractEditorContent({
		html,
		editorPayload,
		editorRef,
		editorElement,
	});

	let headerLogoSources = [];
	if (Array.isArray(logos?.headers)) {
		headerLogoSources = logos.headers.filter(Boolean);
	} else if (logos?.header) {
		headerLogoSources = [logos.header];
	}

	const pdf = new JsPDF({
		orientation: finalLayout.orientation,
		unit: finalLayout.unit,
		format: finalLayout.pageFormat,
	});

	const pageWidth = pdf.internal.pageSize.getWidth();
	const pageHeight = pdf.internal.pageSize.getHeight();

	const styles = getDocumentStyles({
		...styleOptions,
		pageWidthMm: pageWidth,
		pageHeightMm: pageHeight,
		marginTop: finalLayout.marginTop,
		marginRight: finalLayout.marginRight,
		marginBottom: finalLayout.marginBottom,
		marginLeft: finalLayout.marginLeft,
		headerGap: finalLayout.headerGap,
		footerGap: finalLayout.footerGap,
	});

	const headerHtml = getHeaderTemplate({
		logoSrcs: headerLogoSources,
	});

	const printableHtml = buildDocumentHTML({
		headerHtml,
		contentHtml: editorHtml,
		styles,
	});

	const mountPoint = createWorkContainer(printableHtml);

	try {
		const headerNode = mountPoint.querySelector('[data-pdf-part="header"]');
		const contentNode = mountPoint.querySelector('[data-pdf-part="content"]');
		const pagesNode = mountPoint.querySelector('[data-pdf-part="pages"]');

		if (!headerNode || !contentNode || !pagesNode) {
			throw new Error("No se pudo construir la estructura HTML para exportar PDF.");
		}

		normalizeDynamicAutofillForPDF(contentNode);

		await waitForResources(mountPoint);
		paginateBlocks({
			headerNode,
			contentNode,
			pagesNode,
		});
		normalizeDynamicAutofillForPDF(pagesNode);
		await waitForResources(pagesNode);

		const pageNodes = Array.from(pagesNode.querySelectorAll(".siis-pdf-page"));
		if (!pageNodes.length) {
			throw new Error("No se pudieron construir paginas PDF a partir del contenido.");
		}

		for (let index = 0; index < pageNodes.length; index += 1) {
			const pageNode = pageNodes[index];
			const pageCanvas = await html2canvas(pageNode, {
				scale: finalLayout.contentScale,
				useCORS: true,
				backgroundColor: "#ffffff",
			});
			const pageImage = pageCanvas.toDataURL("image/png", 1.0);

			if (index > 0) {
				pdf.addPage();
			}

			pdf.addImage(pageImage, "PNG", 0, 0, pageWidth, pageHeight, undefined, "FAST");
		}

		if (autoSave) {
			pdf.save(outputFileName);
		}
		return pdf;
	} finally {
		destroyWorkContainer(mountPoint);
	}
};

export default contentToPDF;
